import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { SignalDashboardBucket } from "../../../api/client.ts";
import { formatNumber, utcDayLabel } from "../../../utils/format.ts";

const DAY = 86_400_000;
const PRIMARY = "var(--mui-palette-primary-main)";
const SURFACE = "var(--mui-palette-background-default)";
const MUTED = "var(--mui-palette-text-secondary)";
const GRID = "var(--mui-palette-divider)";

export interface TimelinePoint {
  t: number;
  matched: number;
  qualified: number;
  other: number;
}

/** Start (UTC) of the day or ISO week holding `t`, as the backend's date_trunc. */
function bucketStart(t: number, bucket: "day" | "week"): number {
  const day = Math.floor(t / DAY) * DAY;
  if (bucket === "day") return day;
  return day - ((new Date(day).getUTCDay() + 6) % 7) * DAY;
}

/**
 * Every bucket from the period's start (or the first signal, for all time)
 * to now, with zeros where nothing matched.
 */
export function timelinePoints(
  timeline: SignalDashboardBucket[],
  bucket: "day" | "week",
  since: string | undefined,
): TimelinePoint[] {
  const byStart = new Map(timeline.map((b) => [bucketStart(Date.parse(b.start), bucket), b]));
  const first = since
    ? bucketStart(Date.parse(since), bucket)
    : timeline.length
      ? bucketStart(Date.parse(timeline[0].start), bucket)
      : null;
  if (first === null) return [];
  const step = bucket === "day" ? DAY : 7 * DAY;
  const points: TimelinePoint[] = [];
  for (let t = first; t <= bucketStart(Date.now(), bucket); t += step) {
    const b = byStart.get(t);
    const matched = b?.matched ?? 0;
    const qualified = b?.qualified_or_verified ?? 0;
    points.push({ t, matched, qualified, other: matched - qualified });
  }
  return points;
}

function ChartTooltip({
  active,
  payload,
  bucket,
}: {
  active?: boolean;
  payload?: { payload: TimelinePoint }[];
  bucket: "day" | "week";
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Paper elevation={4} sx={{ px: 1.5, py: 1, borderRadius: 2, minWidth: 170 }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {bucket === "week" ? `Week of ${utcDayLabel(p.t)}` : utcDayLabel(p.t)}
      </Typography>
      <LegendRow color={PRIMARY} label="Qualified or verified" value={p.qualified} />
      <LegendRow color={PRIMARY} faded label="Other matches" value={p.other} />
    </Paper>
  );
}

function LegendRow({
  color,
  faded,
  label,
  value,
}: {
  color: string;
  faded?: boolean;
  label: string;
  value?: number;
}) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Box
        sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: color, opacity: faded ? 0.3 : 1 }}
      />
      <Typography variant="body2" sx={{ flex: 1 }}>
        {label}
      </Typography>
      {value !== undefined && (
        <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {formatNumber(value)}
        </Typography>
      )}
    </Stack>
  );
}

/** Matches per day or week, the qualified or verified ones at the base of each column. */
export default function SignalsTimelineChart({
  points,
  bucket,
}: {
  points: TimelinePoint[];
  bucket: "day" | "week";
}) {
  return (
    <Box>
      <Stack direction="row" spacing={2.5} sx={{ mb: 1.5 }}>
        <LegendRow color={PRIMARY} label="Qualified or verified" />
        <LegendRow color={PRIMARY} faded label="Other matches" />
      </Stack>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={points} margin={{ top: 4, right: 0, bottom: 0, left: -12 }} barCategoryGap="20%">
          <CartesianGrid vertical={false} stroke={GRID} strokeOpacity={0.6} />
          <XAxis
            dataKey="t"
            tickFormatter={utcDayLabel}
            tick={{ fill: MUTED, fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: GRID }}
            minTickGap={24}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: MUTED, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={40}
          />
          <Tooltip
            cursor={{ fill: "var(--mui-palette-action-hover)" }}
            content={<ChartTooltip bucket={bucket} />}
            isAnimationActive={false}
          />
          <Bar
            dataKey="qualified"
            stackId="m"
            fill={PRIMARY}
            stroke={SURFACE}
            strokeWidth={1}
            isAnimationActive={false}
          />
          <Bar
            dataKey="other"
            stackId="m"
            fill={PRIMARY}
            fillOpacity={0.28}
            stroke={SURFACE}
            strokeWidth={1}
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
